// FILE: src/components/Admin/AI/rag/RagOverviewPanel.tsx
import React from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  normalizeItems,
  useFetchProductRagPipelineMappings,
  useFetchRagPipelineIngestionRuns,
  useFetchRagPipelineGroups,
  useFetchRagPipelines,
} from '../../../../hooks/rag-hooks';

import {
  dateLabel,
  SectionHeader,
  StatCard,
  statusChip,
} from './ragAdminUtils';

const getRunStatus = (run: any) =>
  run?.status || run?.state || run?.result || 'unknown';

const getRunLabel = (run: any) =>
  run?.pipeline_group?.name ||
  run?.pipeline_group_name ||
  run?.pipeline_group_key ||
  run?.group_name ||
  run?.pipeline_name ||
  `Run ${run?.id ?? ''}`;

const pipelineSummary = (pipeline: any, fallbackId?: any) => {
  if (!pipeline) {
    return fallbackId ? `Pipeline ${fallbackId}` : '-';
  }

  if (typeof pipeline === 'string' || typeof pipeline === 'number') {
    return `Pipeline ${pipeline}`;
  }

  return pipeline.name || pipeline.key || pipeline.display_name || `Pipeline ${pipeline.id ?? fallbackId ?? '-'}`;
};

const pipelineExternalId = (pipeline: any) => {
  if (!pipeline || typeof pipeline !== 'object') return '-';
  return pipeline.provider_pipeline_id || pipeline.gcs_bucket || pipeline.pubsub_topic || '-';
};

const pipelineStatus = (pipeline: any) => {
  if (!pipeline || typeof pipeline !== 'object') return 'unknown';
  return pipeline.status || 'unknown';
};

const activePipelineForGroup = (group: any) => {
  if (group?.active_pipeline) return group.active_pipeline;

  if (group?.active_slot === 'green') {
    return group?.green_pipeline_detail || group?.green_pipeline;
  }

  return group?.blue_pipeline_detail || group?.blue_pipeline;
};

const ingestPipelineForGroup = (group: any) => {
  if (group?.ingest_pipeline) return group.ingest_pipeline;

  if (group?.ingest_slot === 'blue') {
    return group?.blue_pipeline_detail || group?.blue_pipeline;
  }

  return group?.green_pipeline_detail || group?.green_pipeline;
};

const profileUsage = (pipeline: any) => {
  const blue = pipeline?.used_by_profiles?.blue || [];
  const green = pipeline?.used_by_profiles?.green || [];

  if (!blue.length && !green.length) return '-';

  const parts = [];

  if (blue.length) {
    parts.push(`Blue: ${blue.map((item: any) => item.key || item.name || item.id).join(', ')}`);
  }

  if (green.length) {
    parts.push(`Green: ${green.map((item: any) => item.key || item.name || item.id).join(', ')}`);
  }

  return parts.join(' | ');
};

const RagOverviewPanel: React.FC = () => {
  const groupsQuery = useFetchRagPipelineGroups(
    {
      size: 500,
      detailed: true,
      depth: 2,
      'order-by': 'name',
    },
    {
      staleTime: 30_000,
    },
  );

  const pipelinesQuery = useFetchRagPipelines(
    {
      size: 500,
      detailed: true,
      depth: 2,
      'order-by': 'name',
    },
    {
      staleTime: 30_000,
    },
  );

  const mappingsQuery = useFetchProductRagPipelineMappings(
    {
      size: 500,
      detailed: true,
      depth: 2,
      'order-by': 'priority',
    },
    {
      staleTime: 30_000,
    },
  );

  const runsQuery = useFetchRagPipelineIngestionRuns(
    {
      size: 10,
      detailed: true,
      depth: 2,
      'order-by': '-created',
    },
    {
      staleTime: 30_000,
    },
  );

  const isLoading =
    groupsQuery.isLoading ||
    pipelinesQuery.isLoading ||
    mappingsQuery.isLoading ||
    runsQuery.isLoading;

  const isFetching =
    groupsQuery.isFetching ||
    pipelinesQuery.isFetching ||
    mappingsQuery.isFetching ||
    runsQuery.isFetching;

  const error =
    groupsQuery.error ||
    pipelinesQuery.error ||
    mappingsQuery.error ||
    runsQuery.error;

  const groups = normalizeItems<any>(groupsQuery.data);
  const pipelines = normalizeItems<any>(pipelinesQuery.data);
  const mappings = normalizeItems<any>(mappingsQuery.data);
  const runs = normalizeItems<any>(runsQuery.data);

  const activeGroups = groups.filter(group => {
    if (typeof group?.enabled === 'boolean') return group.enabled;
    if (typeof group?.is_active === 'boolean') return group.is_active;
    return true;
  });

  const enabledPipelines = pipelines.filter(pipeline => pipeline?.enabled !== false);

  const mappedProducts = new Set(
    mappings
      .map(mapping => {
        const product = mapping?.product;

        if (typeof product === 'number' || typeof product === 'string') return product;

        return mapping?.product_id || product?.id || product?.Id;
      })
      .filter(Boolean),
  );

  const recentRuns = runs.slice(0, 10);

  return (
    <Box>
      <SectionHeader
        title="RAG Overview"
        description="Summary of RAG pipeline profiles, concrete provider pipelines, product mappings, and recent ingestion activity."
      />

      {isLoading ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading RAG overview…
          </Typography>
        </Stack>
      ) : null}

      {!isLoading && isFetching ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ my: 2 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Refreshing RAG overview…
          </Typography>
        </Stack>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          Failed to load one or more RAG overview datasets:{' '}
          {(error as Error)?.message || 'Unknown error'}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ my: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Pipeline Profiles"
            value={groups.length}
            caption={`${activeGroups.length} enabled`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Concrete Pipelines"
            value={pipelines.length}
            caption={`${enabledPipelines.length} enabled`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Product Mappings"
            value={mappings.length}
            caption={`${mappedProducts.size} unique products`}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Recent Runs"
            value={runs.length}
            caption="Latest ingestion records"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
                <Typography variant="h6">
                  Pipeline Profiles and Active Routing
                </Typography>
                <Chip
                  size="small"
                  color="info"
                  variant="outlined"
                  label="Routing uses profile active_slot, not legacy slot rows"
                />
              </Stack>

              {groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No pipeline profiles found.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 1100 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Profile</TableCell>
                        <TableCell width={110}>Provider</TableCell>
                        <TableCell>Active Pipeline</TableCell>
                        <TableCell>Ingest Pipeline</TableCell>
                        <TableCell width={170}>Slots</TableCell>
                        <TableCell width={120}>Enabled</TableCell>
                        <TableCell width={180}>Updated</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {groups.slice(0, 10).map(group => {
                        const activePipeline = activePipelineForGroup(group);
                        const ingestPipeline = ingestPipelineForGroup(group);

                        return (
                          <TableRow key={group.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>
                                {group.name || group.key}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {group.key}
                              </Typography>
                              {group.description ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {group.description}
                                </Typography>
                              ) : null}
                            </TableCell>

                            <TableCell>
                              <Chip size="small" variant="outlined" label={group.provider || '-'} />
                            </TableCell>

                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography variant="body2" fontWeight={700}>
                                  {pipelineSummary(activePipeline)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                  {pipelineExternalId(activePipeline)}
                                </Typography>
                                <Box>{statusChip(pipelineStatus(activePipeline))}</Box>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Stack spacing={0.25}>
                                <Typography variant="body2" fontWeight={700}>
                                  {pipelineSummary(ingestPipeline)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                  {pipelineExternalId(ingestPipeline)}
                                </Typography>
                                <Box>{statusChip(pipelineStatus(ingestPipeline))}</Box>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Stack spacing={0.5}>
                                <Chip
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  label={`Active: ${group.active_slot || '-'}`}
                                />
                                <Chip
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  label={`Ingest: ${group.ingest_slot || '-'}`}
                                />
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                color={group.enabled ? 'success' : 'default'}
                                variant="outlined"
                                label={group.enabled ? 'enabled' : 'disabled'}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {dateLabel(group.last_updated || group.updated || group.created)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Concrete Pipelines
              </Typography>

              {pipelines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No concrete pipelines found.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 850 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Pipeline</TableCell>
                        <TableCell width={110}>Provider</TableCell>
                        <TableCell width={130}>Status</TableCell>
                        <TableCell>Profile Usage</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pipelines.slice(0, 8).map(pipeline => (
                        <TableRow key={pipeline.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700}>
                              {pipeline.name || pipeline.key}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pipeline.key}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              sx={{ wordBreak: 'break-word' }}
                            >
                              {pipeline.provider_pipeline_id || pipeline.gcs_bucket || pipeline.pubsub_topic || '-'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip size="small" variant="outlined" label={pipeline.provider || '-'} />
                          </TableCell>

                          <TableCell>{statusChip(pipeline.status || 'unknown')}</TableCell>

                          <TableCell>
                            <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                              {profileUsage(pipeline)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Ingestion Runs
              </Typography>

              {recentRuns.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No ingestion runs found.
                </Typography>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 850 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Pipeline</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Target</TableCell>
                        <TableCell>Started</TableCell>
                        <TableCell>Completed</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {recentRuns.map(run => (
                        <TableRow key={run?.id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {getRunLabel(run)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {run.pipeline_name || run.pipeline_key || run.pipeline_provider_pipeline_id || '-'}
                            </Typography>
                          </TableCell>

                          <TableCell>{statusChip(getRunStatus(run))}</TableCell>

                          <TableCell>
                            <Typography variant="caption">
                              {run.product_name || run.go_to_market_name || run.target_type || '-'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {dateLabel(run?.started_at || run?.created)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {dateLabel(run?.completed_at || run?.finished_at || run?.last_updated)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Routing Model
              </Typography>

              <Typography variant="body2" color="text.secondary">
                RAG routing is resolved through pipeline profiles. Each profile maps a blue and green concrete pipeline.
                Runtime reads from the profile active slot. Ingestion writes to the profile ingest slot. Legacy
                RagPipelineSlot rows may still exist for compatibility, but they are not the authoritative routing state.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RagOverviewPanel;